import React, { useState, useEffect, useRef } from "react";
import api from "../../api/api";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";

import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

const VerifyCode = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  const email = location.state?.email || "";

  useEffect(() => {
    if (!email) {
      setMessage(
        "No email address was found. Please request a new verification code."
      );
      setMessageType("error");
    }
  }, [email]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (loading || verified) return;

    if (!email) {
      setMessage("Please return to password recovery and enter your email.");
      setMessageType("error");
      return;
    }

    if (!/^\d{6}$/.test(code)) {
      setMessage("Please enter the complete 6-digit verification code.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/verify-reset-code/", {
        email,
        code,
      });

      setVerified(true);
      setMessage(
        res.data?.message || "Verification successful. Continue to reset your password."
      );
      setMessageType("success");

      navigate("/resetPassword", {
        state: { email, code },
      });
    } catch (err) {
      setMessage(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "The verification code is invalid or has expired. Please try again."
      );
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    .jana-verify-page {
      --vc-purple: #54218f;
      --vc-deep: #35105f;
      --vc-gold: #f4c542;
      --vc-gold-dark: #d6a817;
      --vc-text: #29233a;
      --vc-muted: #777184;

      position: relative;
      isolation: isolate;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      min-height: 100dvh;
      padding: 35px 18px;
      overflow: hidden;
      background:
        radial-gradient(ellipse at 10% 10%, rgba(84, 33, 143, .10), transparent 34%),
        radial-gradient(ellipse at 90% 90%, rgba(244, 197, 66, .15), transparent 35%),
        linear-gradient(145deg, #ffffff 0%, #faf7ff 55%, #f6efff 100%);
      font-family: inherit;
    }

    .jana-verify-page *,
    .jana-verify-page *::before,
    .jana-verify-page *::after {
      box-sizing: border-box;
    }

    .vc-background-shape {
      position: absolute;
      z-index: -1;
      border-radius: 50%;
      pointer-events: none;
      animation: vc-float 13s ease-in-out infinite;
    }

    .vc-shape-one {
      top: -125px;
      left: -100px;
      width: 350px;
      height: 350px;
      border: 55px solid rgba(84, 33, 143, .065);
    }

    .vc-shape-two {
      right: -135px;
      bottom: -100px;
      width: 400px;
      height: 400px;
      border: 65px solid rgba(244, 197, 66, .13);
      animation-delay: -5s;
    }

    .vc-shape-three {
      top: 20%;
      right: 13%;
      width: 95px;
      height: 95px;
      background: rgba(84, 33, 143, .055);
      animation-delay: -3s;
    }

    .vc-shape-four {
      bottom: 17%;
      left: 12%;
      width: 60px;
      height: 60px;
      background: rgba(244, 197, 66, .20);
      animation-delay: -7s;
    }

    @keyframes vc-float {
      0%, 100% {
        transform: translate3d(0, 0, 0);
      }
      50% {
        transform: translate3d(0, -20px, 0);
      }
    }

    .vc-layout {
      display: grid;
      grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr);
      width: 100%;
      max-width: 1030px;
      min-height: 590px;
      overflow: hidden;
      border: 1px solid rgba(84, 33, 143, .12);
      border-radius: 28px;
      background: rgba(255, 255, 255, .93);
      box-shadow:
        0 30px 90px rgba(53, 16, 95, .12),
        0 5px 18px rgba(53, 16, 95, .045);
      backdrop-filter: blur(18px);
    }

    .vc-brand-panel {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-width: 0;
      padding: 42px 35px;
      overflow: hidden;
      color: #ffffff;
      background:
        radial-gradient(circle at 100% 0%, rgba(244, 197, 66, .18), transparent 32%),
        linear-gradient(145deg, #35105f 0%, #54218f 62%, #6730a5 100%);
    }

    .vc-brand-panel::before {
      content: "";
      position: absolute;
      top: 40%;
      right: -115px;
      width: 270px;
      height: 270px;
      border: 1px solid rgba(255, 255, 255, .12);
      border-radius: 50%;
      box-shadow:
        0 0 0 28px rgba(255, 255, 255, .025),
        0 0 0 58px rgba(255, 255, 255, .025);
      pointer-events: none;
    }

    .vc-brand-top,
    .vc-brand-middle,
    .vc-brand-bottom {
      position: relative;
      z-index: 1;
    }

    .vc-brand-top {
      display: flex;
      align-items: center;
      gap: 13px;
    }

    .vc-brand-logo {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 49px;
      height: 49px;
      flex-shrink: 0;
      border: 1px solid rgba(244, 197, 66, .45);
      border-radius: 15px;
      background: rgba(244, 197, 66, .12);
      color: var(--vc-gold);
      font-size: 26px;
    }

    .vc-brand-name {
      margin: 0;
      color: #ffffff;
      font-size: 14px;
      font-weight: 850;
      line-height: 1.5;
      letter-spacing: .4px;
      overflow-wrap: anywhere;
    }

    .vc-brand-caption {
      margin: 4px 0 0;
      color: rgba(255, 255, 255, .67);
      font-size: 10px;
      font-weight: 750;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    .vc-brand-middle {
      margin: 65px 0;
    }

    .vc-brand-eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      color: var(--vc-gold);
      font-size: 10px;
      font-weight: 850;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    .vc-brand-eyebrow::before {
      content: "";
      width: 24px;
      height: 3px;
      border-radius: 5px;
      background: var(--vc-gold);
    }

    .vc-brand-heading {
      margin: 0 0 19px;
      color: #ffffff;
      font-size: clamp(32px, 4vw, 43px);
      font-weight: 850;
      line-height: 1.15;
      letter-spacing: -1.3px;
    }

    .vc-brand-heading span {
      color: var(--vc-gold);
    }

    .vc-brand-description {
      max-width: 340px;
      margin: 0;
      color: rgba(255, 255, 255, .76);
      font-size: 13px;
      line-height: 1.95;
    }

    .vc-brand-points {
      display: grid;
      gap: 15px;
      margin-top: 30px;
    }

    .vc-brand-point {
      display: flex;
      align-items: center;
      gap: 11px;
      color: rgba(255, 255, 255, .90);
      font-size: 12px;
      font-weight: 600;
    }

    .vc-brand-point-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      border-radius: 9px;
      background: rgba(244, 197, 66, .13);
      color: var(--vc-gold);
      font-size: 14px;
    }

    .vc-brand-bottom {
      padding-top: 20px;
      border-top: 1px solid rgba(255, 255, 255, .15);
      color: rgba(255, 255, 255, .58);
      font-size: 10px;
      line-height: 1.8;
    }

    .vc-form-panel {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      padding: 55px 48px;
      background: rgba(255, 255, 255, .96);
    }

    .vc-form-content {
      width: 100%;
      max-width: 390px;
    }

    .vc-form-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 68px;
      height: 68px;
      margin-bottom: 25px;
      border: 1px solid rgba(84, 33, 143, .12);
      border-radius: 21px;
      background: linear-gradient(145deg, #f6efff, #e9dcfa);
      color: var(--vc-purple);
      font-size: 33px;
      box-shadow: 0 8px 22px rgba(84, 33, 143, .09);
    }

    .vc-form-eyebrow {
      margin: 0 0 10px;
      color: #a27b18;
      font-size: 10px;
      font-weight: 850;
      letter-spacing: 1.8px;
      text-transform: uppercase;
    }

    .vc-form-title {
      margin: 0 0 13px;
      color: var(--vc-deep);
      font-size: clamp(29px, 3vw, 36px);
      font-weight: 850;
      line-height: 1.2;
      letter-spacing: -1px;
    }

    .vc-form-description {
      margin: 0;
      color: var(--vc-muted);
      font-size: 13px;
      line-height: 1.9;
    }

    .vc-email-address {
      display: flex;
      align-items: flex-start;
      gap: 11px;
      margin-top: 23px;
      padding: 14px;
      border: 1px solid #e9def5;
      border-radius: 13px;
      background: #faf7ff;
    }

    .vc-email-address-icon {
      flex-shrink: 0;
      margin-top: 2px;
      color: var(--vc-purple);
      font-size: 20px;
    }

    .vc-email-address-label {
      margin: 0 0 4px;
      color: #8d8499;
      font-size: 10px;
      font-weight: 750;
      letter-spacing: .8px;
      text-transform: uppercase;
    }

    .vc-email-address-value {
      margin: 0;
      color: var(--vc-deep);
      font-size: 12px;
      font-weight: 800;
      line-height: 1.6;
      overflow-wrap: anywhere;
    }

    .vc-code-field {
      margin-top: 24px !important;
    }

    .vc-code-field .MuiOutlinedInput-root {
      min-height: 65px;
      border-radius: 13px;
      background: #fcfaff;
    }

    .vc-code-field .MuiOutlinedInput-notchedOutline {
      border-color: #e7dff0;
    }

    .vc-code-field .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: #ad8bd5;
    }

    .vc-code-field .MuiOutlinedInput-root.Mui-focused {
      box-shadow: 0 0 0 4px rgba(84, 33, 143, .08);
    }

    .vc-code-field .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-width: 1.5px;
      border-color: var(--vc-purple);
    }

    .vc-code-input {
      text-align: center;
      font-size: 25px !important;
      font-weight: 850 !important;
      letter-spacing: 12px !important;
      padding-left: 18px !important;
      font-variant-numeric: tabular-nums;
    }

    .vc-code-helper {
      margin: 9px 0 0;
      color: #92899e;
      font-size: 10px;
      line-height: 1.8;
    }

    .vc-primary-button.MuiButton-root {
      min-height: 54px;
      border-radius: 13px;
      background: linear-gradient(110deg, #54218f, #6730a5);
      color: #ffffff;
      font-size: 12px;
      font-weight: 850;
      letter-spacing: .3px;
      text-transform: none;
      box-shadow: 0 9px 22px rgba(84, 33, 143, .20);
      transition:
        transform .2s ease,
        box-shadow .2s ease,
        background .2s ease;
    }

    .vc-primary-button.MuiButton-root:hover {
      transform: translateY(-2px);
      background: linear-gradient(110deg, #35105f, #54218f);
      box-shadow: 0 13px 27px rgba(84, 33, 143, .27);
    }

    .vc-primary-button.MuiButton-root.Mui-disabled {
      background: #b9a5d0;
      color: #ffffff;
    }

    .vc-back-button.MuiButton-root {
      min-height: 46px;
      margin-top: 10px;
      border-radius: 12px;
      color: var(--vc-purple);
      font-size: 12px;
      font-weight: 750;
      text-transform: none;
    }

    .vc-back-button.MuiButton-root:hover {
      background: #f5effb;
    }

    .vc-security-note {
      display: flex;
      align-items: flex-start;
      gap: 11px;
      margin-top: 25px;
      padding: 15px;
      border: 1px solid #eee6f6;
      border-radius: 13px;
      background: #faf7ff;
    }

    .vc-security-note-icon {
      flex-shrink: 0;
      margin-top: 2px;
      color: var(--vc-purple);
      font-size: 19px;
    }

    .vc-security-note p {
      margin: 0;
      color: #777184;
      font-size: 11px;
      line-height: 1.8;
    }

    .vc-security-note strong {
      color: var(--vc-deep);
    }

    .vc-form-footer {
      margin-top: 27px;
      padding-top: 19px;
      border-top: 1px solid #eee8f4;
      color: #948ba0;
      font-size: 10px;
      line-height: 1.8;
      text-align: center;
    }

    .vc-form-footer span {
      color: var(--vc-purple);
      font-weight: 800;
    }

    .vc-alert {
      margin-top: 18px;
      border-radius: 12px !important;
      font-size: 12px !important;
      overflow-wrap: anywhere;
    }

    @media (max-width: 800px) {
      .vc-layout {
        grid-template-columns: minmax(0, 1fr);
        max-width: 510px;
        min-height: auto;
      }

      .vc-brand-panel {
        padding: 27px 28px;
      }

      .vc-brand-middle {
        margin: 34px 0;
      }

      .vc-brand-heading {
        max-width: 400px;
        font-size: 34px;
      }

      .vc-brand-description {
        max-width: 430px;
      }

      .vc-brand-points {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }

      .vc-brand-bottom {
        margin-top: 5px;
      }

      .vc-form-panel {
        padding: 39px 28px;
      }

      .vc-form-content {
        max-width: 440px;
      }

      .vc-shape-three {
        right: 4%;
      }
    }

    @media (max-width: 450px) {
      .jana-verify-page {
        padding: 15px 11px;
      }

      .vc-layout {
        border-radius: 21px;
      }

      .vc-brand-panel {
        padding: 24px 21px;
      }

      .vc-brand-heading {
        font-size: 30px;
      }

      .vc-brand-middle {
        margin: 29px 0;
      }

      .vc-brand-points {
        grid-template-columns: minmax(0, 1fr);
      }

      .vc-form-panel {
        padding: 32px 21px;
      }

      .vc-form-title {
        font-size: 29px;
      }

      .vc-form-icon {
        width: 60px;
        height: 60px;
        font-size: 29px;
      }

      .vc-code-input {
        font-size: 22px !important;
        letter-spacing: 9px !important;
        padding-left: 13px !important;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .vc-background-shape {
        animation: none !important;
      }

      .vc-primary-button.MuiButton-root {
        transition: none !important;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <Box className="jana-verify-page">
        <Box
          className="vc-background-shape vc-shape-one"
          aria-hidden="true"
        />
        <Box
          className="vc-background-shape vc-shape-two"
          aria-hidden="true"
        />
        <Box
          className="vc-background-shape vc-shape-three"
          aria-hidden="true"
        />
        <Box
          className="vc-background-shape vc-shape-four"
          aria-hidden="true"
        />

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 1030, zIndex: 1 }}
        >
          <Paper className="vc-layout" elevation={0}>
            {/* BRAND PANEL */}
            <Box className="vc-brand-panel">
              <Box className="vc-brand-top">
                <Box className="vc-brand-logo">
                  <SchoolRoundedIcon fontSize="inherit" />
                </Box>

                <Box>
                  <Typography className="vc-brand-name">
                    School Portal
                  </Typography>

                  <Typography className="vc-brand-caption">
                    Learning • Growth • Excellence
                  </Typography>
                </Box>
              </Box>

              <Box className="vc-brand-middle">
                <Typography className="vc-brand-eyebrow">
                  Secure Verification
                </Typography>

                <Typography component="h2" className="vc-brand-heading">
                  One more step
                  <br />
                  to <span>your account.</span>
                </Typography>

                <Typography className="vc-brand-description">
                  Verify your identity with the six-digit code sent to
                  your email address. Once verified, you can continue
                  resetting your password securely.
                </Typography>

                <Box className="vc-brand-points">
                  <Box className="vc-brand-point">
                    <Box className="vc-brand-point-icon">
                      <ShieldOutlinedIcon fontSize="inherit" />
                    </Box>
                    Secure identity verification
                  </Box>

                  <Box className="vc-brand-point">
                    <Box className="vc-brand-point-icon">
                      <EmailOutlinedIcon fontSize="inherit" />
                    </Box>
                    Email-based confirmation
                  </Box>

                  <Box className="vc-brand-point">
                    <Box className="vc-brand-point-icon">
                      <LockResetRoundedIcon fontSize="inherit" />
                    </Box>
                    Protected password reset
                  </Box>

                  <Box className="vc-brand-point">
                    <Box className="vc-brand-point-icon">
                      <AutoAwesomeRoundedIcon fontSize="inherit" />
                    </Box>
                    Simple recovery process
                  </Box>
                </Box>
              </Box>

              <Box className="vc-brand-bottom">
                Never share your verification code. Enter it only on
                your official school portal.
              </Box>
            </Box>

            {/* VERIFICATION FORM */}
            <Box className="vc-form-panel">
              <Box className="vc-form-content">
                <Box className="vc-form-icon">
                  <VerifiedUserRoundedIcon fontSize="inherit" />
                </Box>

                <Typography className="vc-form-eyebrow">
                  Identity Confirmation
                </Typography>

                <Typography component="h1" className="vc-form-title">
                  Verify Your Code
                </Typography>

                <Typography className="vc-form-description">
                  Enter the six-digit verification code sent to your
                  registered email address to continue.
                </Typography>

                {email && (
                  <Box className="vc-email-address">
                    <EmailOutlinedIcon
                      className="vc-email-address-icon"
                      aria-hidden="true"
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography className="vc-email-address-label">
                        Verification email sent to
                      </Typography>

                      <Typography className="vc-email-address-value">
                        {email}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!email && (
                  <Alert severity="warning" className="vc-alert">
                    No email address was provided. Return to password
                    recovery to request a verification code.
                  </Alert>
                )}

                {email && (
                  <Alert severity="info" className="vc-alert">
                    Your verification code expires after 10 minutes.
                  </Alert>
                )}

                {message && (
                  <Alert
                    severity={messageType}
                    className="vc-alert"
                    onClose={() => setMessage("")}
                  >
                    {message}
                  </Alert>
                )}

                <Box
                  component="form"
                  onSubmit={handleVerify}
                  noValidate
                  sx={{ mt: 1 }}
                >
                  <TextField
                    className="vc-code-field"
                    fullWidth
                    required
                    label="6-Digit Verification Code"
                    placeholder="000000"
                    value={code}
                    disabled={loading || verified || !email}
                    inputRef={inputRef}
                    autoComplete="one-time-code"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SecurityRoundedIcon
                            sx={{ color: "#54218f", fontSize: 21 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      maxLength: 6,
                      inputMode: "numeric",
                      pattern: "[0-9]{6}",
                      "aria-label": "Six-digit verification code",
                      className: "vc-code-input",
                    }}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);

                      setCode(value);
                      setMessage("");
                    }}
                  />

                  <Typography className="vc-code-helper">
                    Enter all six digits. Numbers only.
                  </Typography>

                  <Button
                    className="vc-primary-button"
                    fullWidth
                    type="submit"
                    disabled={
                      loading ||
                      verified ||
                      !email ||
                      code.length !== 6
                    }
                    endIcon={
                      !loading ? <ArrowForwardRoundedIcon /> : undefined
                    }
                    sx={{ mt: 2.5 }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress
                          size={20}
                          sx={{ color: "#fff", mr: 1.5 }}
                        />
                        Verifying Code...
                      </>
                    ) : (
                      "Verify and Continue"
                    )}
                  </Button>

                  <Button
                    className="vc-back-button"
                    fullWidth
                    type="button"
                    startIcon={<ArrowBackRoundedIcon />}
                    disabled={loading}
                    onClick={() => navigate("/forgotPassword")}
                  >
                    Back to Password Recovery
                  </Button>
                </Box>

                <Box className="vc-security-note">
                  <ShieldOutlinedIcon className="vc-security-note-icon" />

                  <Typography component="p">
                    <strong>Keep your account secure</strong>
                    <br />
                    Never share your verification code with anyone.
                    If your code expires, return to password recovery
                    to request another one.
                  </Typography>
                </Box>

                <Box className="vc-form-footer">
                  Secure Account Verification
                  <br />
                  <span>School Portal</span>
                  {" "}• Designed for a better learning experience
                </Box>
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </>
  );
};

export default VerifyCode;
